import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEventImageUrl } from '../lib/image-sas';
import type { ExtractedEvent, SourceType } from '../types';
import { visibleChipTags, formatTag } from '../lib/tagTaxonomy';
import { eventDatePart, formatRange } from '../lib/eventDates';
import SubscribeButton from './SubscribeButton';
import EventReactionButtons from './EventReactionButtons';
import EntityFollowChip from './EntityFollowChip';
import MatchBadge from './MatchBadge';
import { useTasteMatch } from '../lib/tasteMatch';
import { StaleRecurringChip } from './StaleRecurringChip';
import EventReviewWarningChips from './EventReviewWarningChips';
import { useModalHistory } from '../lib/useModalHistory';
import {
  DateProofWarning, dateProofRangeOptions, nonTemporalWarnings, useDateProofEvent, useDateProofReview,
} from '../lib/dateProofReview';

interface Props {
  event: ExtractedEvent | null;
  onClose: () => void;
  /** When provided (admin context), renders an "Edit" button in the header.
   *  The handler is responsible for closing this modal and opening EventEditModal. */
  onEdit?: (event: ExtractedEvent) => void;
  /** When provided (admin context) and the event has a resolved venueId,
   *  renders a "Hide venue" button that marks the venue inactive. The
   *  handler is responsible for confirming with the user and refreshing
   *  the parent list. */
  onHideVenue?: (event: ExtractedEvent) => void;
  /** When provided (admin context) and the event has a resolved seriesId,
   *  renders a "Hide series" button that dismisses + deactivates the series.
   *  Mirrors `onHideVenue` — the handler confirms with the user and refreshes
   *  the parent list. */
  onHideSeries?: (event: ExtractedEvent) => void;
  /** When provided (admin context), renders a "Reject" button that marks
   *  the event as rejected. The handler is responsible for confirming with
   *  the user, refreshing the parent list, and closing this modal. */
  onReject?: (event: ExtractedEvent) => void;
  /** When provided (admin context) and the event has a `recurringSeriesId`,
   *  renders an inline "Fix recurrence" picker that lets the admin shift
   *  every row in the series onto a different weekday. Used to repair
   *  series whose anchor weekday was misread off a calendar grid (e.g. a
   *  weekly Saturday market that the LLM placed on Thursday). The handler
   *  is responsible for calling the API, refreshing the list, and closing
   *  this modal on success. */
  onShiftSeries?: (event: ExtractedEvent, weekday: string) => Promise<void> | void;
  /** Optional callback invoked when an inline tag edit (or other in-modal
   *  mutation) updates the event. Currently a pass-through for callers that
   *  want to keep their list state in sync — the modal itself does not
   *  expose tag editing, so this may be unused at runtime. */
  onEventUpdated?: (event: ExtractedEvent) => void;
}

function formatPostDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

/**
 * Map an event's `sourceType` to a Bootstrap Icons glyph + human label so we
 * can render a small attribution badge in the modal header. Falls back to a
 * generic icon + capitalized type for unknown sources.
 */
function sourceMeta(sourceType?: SourceType | string): { icon: string; label: string } {
  switch (sourceType) {
    case 'instagram':   return { icon: 'bi-instagram',    label: 'Instagram' };
    case 'facebook':    return { icon: 'bi-facebook',     label: 'Facebook' };
    case 'website':     return { icon: 'bi-globe',        label: 'Website' };
    case 'rss':         return { icon: 'bi-rss',          label: 'RSS feed' };
    case 'ical':        return { icon: 'bi-calendar3',    label: 'iCal feed' };
    case 'eventbrite':  return { icon: 'bi-ticket-perforated', label: 'Eventbrite' };
    case 'meetup':      return { icon: 'bi-people',       label: 'Meetup' };
    case 'bandsintown': return { icon: 'bi-music-note-beamed', label: 'Bandsintown' };
    case 'email':       return { icon: 'bi-envelope',     label: 'Email' };
    case 'manual':      return { icon: 'bi-pencil',       label: 'Manual entry' };
    case 'extension':   return { icon: 'bi-puzzle',       label: 'Browser capture' };
    case 'venue':       return { icon: 'bi-shop',         label: 'Venue feed' };
    case 'series':      return { icon: 'bi-collection',   label: 'Series feed' };
    default: {
      const s = String(sourceType ?? '').trim();
      return { icon: 'bi-link-45deg', label: s ? s[0].toUpperCase() + s.slice(1) : 'Source' };
    }
  }
}

/**
 * Lightweight modal with a focus trap on the close button. Built without
 * Bootstrap's JS bundle so we don't have to pull in `bootstrap.bundle.js`
 * just for one dialog.
 */
export default function EventDetailModal({ event: loadedEvent, onClose, onEdit, onHideVenue, onHideSeries, onReject, onShiftSeries, onEventUpdated: _onEventUpdated }: Props) {
  const event = useDateProofEvent(loadedEvent);
  const { open: openDateProof, activeId } = useDateProofReview();
  const proofOpen = useRef(false);
  proofOpen.current = activeId !== null;
  const isOpen = !!event;
  const closeRef = useRef<HTMLButtonElement>(null);
  const resolveImage = useEventImageUrl();
  const imageSrc = resolveImage(event?.imageUrl);
  const tasteMatch = useTasteMatch();
  const match = event ? tasteMatch.match(event) : null;

  // Trap the browser Back button so it closes this modal (restoring the user's
  // place on the page underneath) instead of navigating away. `requestClose`
  // drives every explicit close affordance (X, Escape, backdrop) through the
  // same history-pop path. See lib/useModalHistory for the full contract.
  const requestClose = useModalHistory(!!event, onClose);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !proofOpen.current) requestClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      if (prev?.isConnected) prev.focus();
    };
  }, [isOpen, requestClose]);

  if (!event) return null;

  const hasBadges = !!(event.eventType || event.ageRestriction || event.priceInfo || event.recurringPattern);
  const chipTags = visibleChipTags(event.tags, event);
  const hasTags = chipTags.length > 0;
  const hasPerformers = event.performers && event.performers.length > 0;
  const src = sourceMeta(event.sourceType);
  const venueLabel = event.venueName ?? event.locationText;
  const canHideVenue = !!(onHideVenue && event.venueId && venueLabel);
  const canHideSeries = !!(onHideSeries && event.seriesId && event.seriesName);
  const igUrl = event.venueInstagramHandle
    ? `https://instagram.com/${event.venueInstagramHandle.replace(/^@/, '')}`
    : undefined;
  // Additional sources merged into this event by the compose-event agent
  // (e.g. weekly Riverside Arts Market with separate posts for the Saturday
  // lineup). Parse defensively — bad JSON should never break the modal.
  // Auto-merged sources may carry richer attribution (sourceType, postCreatedAt)
  // when added by the queue dedup pass; older user-added entries only have
  // {url, addedAt, addedByUser?, note?}. Both shapes are valid here.
  const additionalSources: Array<{
    url?: string;
    addedAt?: string;
    note?: string;
    sourceType?: string;
    sourceId?: string;
    rawId?: string;
    postCreatedAt?: string;
  }> = (() => {
    if (!event.additionalSourcesJson) return [];
    try {
      const parsed = JSON.parse(event.additionalSourcesJson);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (s): s is { url?: string; addedAt?: string; note?: string; sourceType?: string; sourceId?: string; rawId?: string; postCreatedAt?: string } =>
          !!s && typeof s === 'object'
      );
    } catch {
      return [];
    }
  })();
  // Hide entries without a usable URL — we can't link them and they'd render as dead buttons.
  const linkableAdditionalSources = additionalSources.filter((s) => typeof s.url === 'string' && s.url.length > 0);
  const hasLinks = !!(event.ticketUrl || event.rawUrl || event.venueWebsite || igUrl || event.venueFacebookUrl) || linkableAdditionalSources.length > 0;

  return (
    <div
      className="mh-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mh-modal-title"
      onClick={requestClose}
    >
      <div className="mh-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="mh-modal-header p-3 border-bottom">
          <div className="d-flex align-items-start gap-2 flex-wrap">
            <div className="flex-grow-1 min-w-0">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h2 id="mh-modal-title" className="h5 mb-0">{event.title}</h2>
                <MatchBadge match={match} showLabel />
                <EventReviewWarningChips warnings={nonTemporalWarnings(event.reviewWarnings)} />
                {event.dateFlaggedWrong && (
                  <span
                    className="badge mh-wrong-date-chip d-inline-flex align-items-center gap-1"
                    title="An admin flagged this date/time as wrong"
                  >
                    <i className="bi bi-calendar-x" aria-hidden="true" />
                    <span>Wrong date</span>
                  </span>
                )}
                <span
                  className="badge rounded-pill text-bg-secondary d-inline-flex align-items-center gap-1"
                  title={`Source: ${src.label}`}
                >
                  <i className={`bi ${src.icon}`} aria-hidden="true" />
                  <span>{src.label}</span>
                </span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-1 flex-shrink-0 mh-modal-actions">
              <EventReactionButtons event={event} />
              <SubscribeButton
                target={{ kind: 'event', id: event.rowKey, title: event.title }}
                className="btn btn-sm btn-icon btn-primary"
                compact
              />
              {onReject && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-outline-danger"
                  onClick={() => onReject(event)}
                  aria-label={`Hide event ${event.title}`}
                  title="Hide event (rejects — removes from public list)"
                >
                  <i className="bi bi-x-circle" aria-hidden="true" />
                </button>
              )}
              {onEdit && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-outline-secondary"
                  onClick={() => onEdit(event)}
                  aria-label={`Edit ${event.title}`}
                  title="Edit event"
                >
                  <i className="bi bi-pencil-square" aria-hidden="true" />
                </button>
              )}
              {canHideSeries && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-outline-danger"
                  onClick={() => onHideSeries!(event)}
                  aria-label={`Hide series ${event.seriesName}`}
                  title={`Dismiss series "${event.seriesName}" (hides all of its events)`}
                >
                  <i className="bi bi-collection" aria-hidden="true" />
                </button>
              )}
              <button
                ref={closeRef}
                type="button"
                className="btn-close ms-1"
                aria-label="Close"
                onClick={requestClose}
              />
            </div>
          </div>
        </header>
        <div className="p-3 d-flex flex-column gap-3">
          {imageSrc && (
            <img src={imageSrc} alt="" className="img-fluid rounded mh-modal-image" />
          )}

          {/* Source links bar — moved up so users can jump to original / venue
              before reading the post text. */}
          {hasLinks && (
            <div className="d-flex flex-wrap gap-2" aria-label="Related links">
              {event.ticketUrl && (
                <a
                  href={event.ticketUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                >
                  <i className="bi bi-ticket-perforated" aria-hidden="true" />
                  Tickets / details
                  <i className="bi bi-box-arrow-up-right small" aria-hidden="true" />
                </a>
              )}
              {event.rawUrl && (
                <a
                  href={event.rawUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                >
                  <i className={`bi ${src.icon}`} aria-hidden="true" />
                  Original post
                  <i className="bi bi-box-arrow-up-right small" aria-hidden="true" />
                </a>
              )}
              {event.venueWebsite && (
                <a
                  href={event.venueWebsite}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                >
                  <i className="bi bi-globe" aria-hidden="true" />
                  Venue website
                  <i className="bi bi-box-arrow-up-right small" aria-hidden="true" />
                </a>
              )}
              {igUrl && (
                <a
                  href={igUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                >
                  <i className="bi bi-instagram" aria-hidden="true" />
                  @{event.venueInstagramHandle!.replace(/^@/, '')}
                  <i className="bi bi-box-arrow-up-right small" aria-hidden="true" />
                </a>
              )}
              {event.venueFacebookUrl && (
                <a
                  href={event.venueFacebookUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                >
                  <i className="bi bi-facebook" aria-hidden="true" />
                  Venue on Facebook
                  <i className="bi bi-box-arrow-up-right small" aria-hidden="true" />
                </a>
              )}
              {linkableAdditionalSources.map((s, i) => {
                const meta = sourceMeta(s.sourceType);
                const datePart = s.postCreatedAt
                  ? formatPostDate(s.postCreatedAt)
                  : (s.addedAt ? formatPostDate(s.addedAt) : '');
                const labelBase = s.sourceType ? meta.label : 'More info';
                const tooltip = [s.note, datePart ? `Posted ${datePart}` : '']
                  .filter(Boolean)
                  .join(' — ') || 'Additional source merged into this event';
                return (
                  <a
                    key={`${s.url}-${i}`}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                    title={tooltip}
                  >
                    <i className={`bi ${meta.icon}`} aria-hidden="true" />
                    {labelBase}{linkableAdditionalSources.length > 1 ? ` #${i + 1}` : ''}
                    {datePart && <span className="text-muted small ms-1">({datePart})</span>}
                    <i className="bi bi-box-arrow-up-right small" aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          )}

          {/* When / where strip — date+location grouped with icons */}
          <div className="mh-modal-meta small">
            <div className="d-flex align-items-start gap-2">
              <i className="bi bi-clock text-muted" aria-hidden="true" />
              <div className="d-flex flex-column align-items-start gap-1">
                <time dateTime={event.startUtc}>
                  {formatRange(event, dateProofRangeOptions(event, { date: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' } }))}
                </time>
                <DateProofWarning event={event} />
                {!event.reviewWarnings?.some((warning) => warning === 'check-date' || warning === 'time-unverified') && (
                  <button type="button" className="btn btn-link mh-date-proof-link"
                    onClick={() => openDateProof(event)}>
                    Date and time evidence / history
                  </button>
                )}
              </div>
            </div>
            {venueLabel && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <i className="bi bi-geo-alt text-muted" aria-hidden="true" />
                <span>
                  {event.venueId ? (
                    <Link to={`/venues/${event.venueId}`} onClick={onClose}>
                      {venueLabel}
                    </Link>
                  ) : (
                    <span>{venueLabel}</span>
                  )}
                  {event.neighborhood && <span className="text-muted"> · {event.neighborhood}</span>}
                </span>
                {event.venueId && (
                  <EntityFollowChip
                    kind="venue"
                    targetId={event.venueId}
                    targetName={venueLabel}
                    stopPropagation={false}
                    offLabel
                  />
                )}
                {canHideVenue && (
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-outline-danger ms-1"
                    onClick={() => onHideVenue!(event)}
                    aria-label={`Hide venue ${venueLabel}`}
                    title={`Hide venue "${venueLabel}" — hides all of its events from the public list.`}
                  >
                    <i className="bi bi-eye-slash" aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Series */}
          {event.seriesId && event.seriesName && (
            <div className="small">
              <span className="text-muted me-1">Part of:</span>
              <Link
                to={`/series/${event.seriesId}`}
                onClick={onClose}
                className="badge rounded-pill text-bg-info text-dark text-decoration-none"
              >
                <i className="bi bi-collection me-1" aria-hidden="true" />
                {event.seriesName}
              </Link>
              <EntityFollowChip
                kind="series"
                targetId={event.seriesId}
                targetName={event.seriesName}
                stopPropagation={false}
                offLabel
                className="ms-2"
              />
            </div>
          )}

          {/* Primary badges */}
          {hasBadges && (
            <div className="d-flex flex-wrap gap-1">
              {event.eventType && (
                <span className="badge text-bg-primary">{formatTag(event.eventType)}</span>
              )}
              {event.ageRestriction && (
                <span className="badge text-bg-warning text-dark">{formatTag(event.ageRestriction)}</span>
              )}
              {event.priceInfo && (
                <span className="badge text-bg-success">{event.priceInfo}</span>
              )}
              {event.recurringPattern && event.recurringPattern !== 'one-time' && (
                <span
                  className="badge text-bg-info text-dark"
                  title={
                    event.recurringDaysOfWeek?.length
                      ? `Recurs ${formatTag(event.recurringPattern)} on ${event.recurringDaysOfWeek.join(', ')}`
                      : `Recurs ${formatTag(event.recurringPattern)}`
                  }
                >
                  🔁 {formatTag(event.recurringPattern)}
                  {event.recurringDaysOfWeek?.length
                    ? ` · ${event.recurringDaysOfWeek.map((d) => d[0].toUpperCase() + d.slice(1, 3)).join(', ')}`
                    : ''}
                </span>
              )}
              <StaleRecurringChip event={event} />
            </div>
          )}

          {/* Admin: shift the entire recurring series onto a different weekday. */}
          {onShiftSeries && event.recurringSeriesId && (
            <ShiftSeriesPicker event={event} onShiftSeries={onShiftSeries} />
          )}

          {/* Tags */}
          {hasTags && (
            <div className="d-flex flex-wrap gap-1" aria-label="Event tags">
              {chipTags.map((tag) => (
                <span key={tag} className="badge rounded-pill text-bg-light border small">{formatTag(tag)}</span>
              ))}
            </div>
          )}

          {/* AI summary — clearly labeled and visually distinct from the verbatim post text below. */}
          {event.aiSummary && (
            <div className="mh-modal-ai-summary p-2 ps-3 border-start border-3 border-info rounded-end">
              <p className="text-uppercase text-muted small fw-semibold mb-1" aria-label="AI-generated summary">
                <i className="bi bi-stars me-1" aria-hidden="true" />
                AI summary
              </p>
              <p className="mb-0 mh-modal-desc">{event.aiSummary}</p>
            </div>
          )}

          {/* Description — verbatim text from the original source. Preserves newlines. */}
          {event.description && (
            <p className="mb-0 mh-modal-desc" style={{ whiteSpace: 'pre-wrap' }}>
              {event.description}
            </p>
          )}

          {/* Performers */}
          {hasPerformers && (
            <p className="small mb-0">
              <strong>Featuring: </strong>{event.performers!.join(', ')}
            </p>
          )}

          {/* Organizer */}
          {event.organizer && (
            <p className="small mb-0">
              <strong>Presented by: </strong>{event.organizer}
            </p>
          )}

          {/* Post metadata footer. When `originalPostCreatedAt` is set and
              differs from the current primary `postCreatedAt`, this event
              had its primary attribution refreshed by a newer post merging
              into it (see queue-extract-event pre-write merge). */}
          {event.postCreatedAt && event.originalPostCreatedAt && event.originalPostCreatedAt !== event.postCreatedAt ? (
            <p className="text-muted small mb-0">
              Originally posted: {formatPostDate(event.originalPostCreatedAt)}
              {' — refreshed '}
              {formatPostDate(event.postCreatedAt)}
            </p>
          ) : event.postCreatedAt && (
            <p className="text-muted small mb-0">
              Originally posted: {formatPostDate(event.postCreatedAt)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const WEEKDAY_OPTIONS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

/**
 * Inline admin-only "Fix recurrence" form rendered just below the
 * recurring badge. The current weekday is derived from the event's New York
 * calendar date (or its source date label for all-day rows). On submit, defers to the
 * parent `onShiftSeries` handler which owns the API call + list refresh.
 */
function ShiftSeriesPicker({
  event,
  onShiftSeries
}: {
  event: ExtractedEvent;
  onShiftSeries: (event: ExtractedEvent, weekday: string) => Promise<void> | void;
}) {
  const currentWeekday = eventDatePart(event, { weekday: 'long' }).toLowerCase();
  const [target, setTarget] = useState<string>(
    event.recurringDaysOfWeek?.[0]?.toLowerCase() ?? currentWeekday
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = target !== currentWeekday;

  async function handleSubmit() {
    setBusy(true);
    setError(null);
    try {
      await onShiftSeries(event, target);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border rounded p-2 bg-light-subtle small">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <label htmlFor="mh-shift-series-weekday" className="fw-semibold mb-0">
          Fix recurrence:
        </label>
        <span className="text-muted">
          Currently {currentWeekday[0].toUpperCase() + currentWeekday.slice(1)} —
        </span>
        <select
          id="mh-shift-series-weekday"
          className="form-select form-select-sm w-auto"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          disabled={busy}
        >
          {WEEKDAY_OPTIONS.map((d) => (
            <option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleSubmit}
          disabled={busy || !dirty || currentWeekday === '—'}
          title={dirty ? 'Shift every event in this series onto the selected weekday' : 'Pick a different weekday to enable'}
        >
          {busy ? 'Shifting…' : 'Apply to whole series'}
        </button>
      </div>
      <p className="text-muted mb-0 mt-1">
        Updates every occurrence sharing this event&rsquo;s recurring series
        and re-publishes them to Google Calendar. Time of day is preserved.
      </p>
      {error && <p className="text-danger mb-0 mt-1">{error}</p>}
    </div>
  );
}
